import React from "react";
import { Navigate } from "react-router-dom";
import { NoPermissionsPage } from "../pages/NoPermissionsPage";
import { Dispatcher } from "../pages/Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import {
  LandingRouteOptions,
  ScreenKey,
  isScreenVisible,
  landingRouteFor,
  readVisibleScreens,
} from "./viewOnlyAccess";

/**
 * Route wrapper for the View Only screen list (ENG-1816). Wraps the positive
 * branch of a route's existing gate: when the screen is in the user's
 * `visible_screens` (or the list is empty = View All) it renders its children
 * unchanged; when the screen is hidden it redirects to the user's landing
 * route rather than rendering an empty page, and falls back to the
 * no-permissions page when nothing is visible at all.
 *
 * This keeps the UI consistent with the saved list. The server still decides
 * what data a user may read.
 */
export const ScreenGate = ({
  screen,
  permission,
  navBarCollapsed,
  setNavBarCollapsed,
  landingOptions,
  children,
}: {
  screen: ScreenKey;
  permission: unknown;
  navBarCollapsed: boolean;
  setNavBarCollapsed: Dispatcher<boolean>;
  landingOptions?: LandingRouteOptions;
  children: React.ReactElement;
}) => {
  // A patrol-only user's whole app is Patrols and their landing route is
  // /sites_patrol, so hiding that screen would redirect them to the page
  // they are already on, forever. Their screen list can never hide it.
  const flags = (permission ?? {}) as Record<string, unknown>;
  if (screen === "patrols" && flags.patrol_only) {
    return children;
  }
  if (isScreenVisible(readVisibleScreens(permission), screen)) {
    return children;
  }
  const landing = landingRouteFor(permission, landingOptions);
  if (landing) {
    return <Navigate to={landing} replace />;
  }
  return (
    <NoPermissionsPage
      navBarCollapsed={navBarCollapsed}
      setNavBarCollapsed={setNavBarCollapsed}
    />
  );
};
